import React, { useState, useEffect } from 'react';
import { LysepAPI } from '../../contexts/ApiContext';
import { toast } from 'react-toastify';
import {
    Key,
    Plus,
    Search,
    Trash2,
    Edit2,
    MoreVertical,
    Shield,
    FileText,
    Terminal,
    Loader2,
    X,
    Save
} from 'lucide-react';

const CredentialsModule = () => {
    const [credentials, setCredentials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
    const [currentCredential, setCurrentCredential] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        type: 'ssh-key',
        username: '',
        value: '',
        description: ''
    });

    const [processing, setProcessing] = useState(false);

    // Initial Fetch
    useEffect(() => {
        fetchCredentials();
    }, []);

    const fetchCredentials = async () => {
        setLoading(true);
        try {
            const resp = await LysepAPI.get('/api/credentials');
            if (resp.data.state) {
                setCredentials(resp.data.data);
            } else {
                toast.error(resp.data.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Kimlik bilgileri yüklenirken hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (mode, credential = null) => {
        setModalMode(mode);
        setCurrentCredential(credential);
        if (mode === 'edit' && credential) {
            setFormData({
                name: credential.name,
                type: credential.type,
                username: credential.username || '',
                value: credential.value || '', // Note: API might not return full value for security? Actually it usually does for edit if allowed.
                description: credential.description || ''
            });
        } else {
            setFormData({
                name: '',
                type: 'ssh-key',
                username: '',
                value: '',
                description: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentCredential(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);

        try {
            if (modalMode === 'add') {
                const resp = await LysepAPI.post('/api/credentials', formData);
                if (resp.data.state) {
                    toast.success("Kimlik bilgisi başarıyla eklendi.");
                    fetchCredentials();
                    handleCloseModal();
                } else {
                    toast.error(resp.data.message);
                }
            } else {
                const resp = await LysepAPI.put(`/api/credentials/${currentCredential.id}`, {
                    ...formData,
                    id: currentCredential.id
                });
                if (resp.data.state) {
                    toast.success("Kimlik bilgisi güncellendi.");
                    fetchCredentials();
                    handleCloseModal();
                } else {
                    toast.error(resp.data.message);
                }
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "İşlem başarısız.");
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Bu kimlik bilgisini silmek istediğinize emin misiniz?")) return;

        try {
            const resp = await LysepAPI.delete(`/api/credentials/${id}`);
            if (resp.data.state) {
                toast.success("Silindi.");
                setCredentials(prev => prev.filter(c => c.id !== id));
            } else {
                toast.error(resp.data.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Silme işlemi başarısız.");
        }
    };

    // Filter
    const filteredCredentials = credentials.filter(cred =>
        cred.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cred.username && cred.username.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="flex flex-col h-full w-full bg-[#0a0e13] p-6 overflow-hidden">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sticky top-0 bg-[#0a0e13] z-10 py-2">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Key className="w-6 h-6 text-[#fd7000]" />
                        Anahtar & Kimlik Bilgileri
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Sunucu bağlantıları ve API servisleri için kullanılan anahtarlar ve şifreler.</p>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative flex-1 sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Ara..."
                            className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-[#fd7000]/50 transition-colors"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={() => handleOpenModal('add')}
                        className="flex items-center gap-2 px-4 py-2 bg-[#fd7000] hover:bg-[#e66000] text-white rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#fd7000]/20"
                    >
                        <Plus className="w-4 h-4" />
                        Yeni Ekle
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 bg-[#1a1a1a] border border-white/5 rounded-2xl overflow-hidden flex flex-col shadow-xl">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-white/[0.02] border-b border-white/5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    <div className="col-span-1">ID</div>
                    <div className="col-span-3">Başlık</div>
                    <div className="col-span-2">Tür</div>
                    <div className="col-span-2">Kullanıcı</div>
                    <div className="col-span-3">Açıklama</div>
                    <div className="col-span-1 text-right">İşlemler</div>
                </div>

                {/* Table Body */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <Loader2 className="w-8 h-8 text-[#fd7000] animate-spin" />
                        </div>
                    ) : filteredCredentials.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                            <Key className="w-12 h-12 mb-3 opacity-20" />
                            <p>Kayıt bulunamadı.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {filteredCredentials.map((cred) => (
                                <div key={cred.id} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-white/[0.02] transition-colors group">
                                    <div className="col-span-1 text-gray-500 font-mono text-xs">#{cred.id}</div>
                                    <div className="col-span-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
                                                {cred.type.includes('key') ? <Key className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                                            </div>
                                            <span className="text-white font-medium text-sm">{cred.name}</span>
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${cred.type === 'ssh-key' ? 'bg-purple-500/10 border-purple-500/20 text-purple-400' :
                                            cred.type === 'ssh-password' ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400' :
                                                'bg-gray-500/10 border-gray-500/20 text-gray-400'
                                            }`}>
                                            {cred.type}
                                        </span>
                                    </div>
                                    <div className="col-span-2 text-sm text-gray-300 font-mono">
                                        {cred.type === 'api-key' ? '-' : (cred.username || '-')}
                                    </div>
                                    <div className="col-span-3 text-sm text-gray-500 truncate">
                                        {cred.description || '-'}
                                    </div>
                                    <div className="col-span-1 flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleOpenModal('edit', cred)}
                                            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                            title="Düzenle"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(cred.id)}
                                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                            title="Sil"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleCloseModal} />
                    <div className="relative w-full max-w-lg bg-[#15191e] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#1a1a1a]">
                            <h3 className="text-lg font-bold text-white">
                                {modalMode === 'add' ? 'Yeni Kimlik Bilgisi Ekle' : 'Kimlik Bilgisini Düzenle'}
                            </h3>
                            <button onClick={handleCloseModal} className="p-1 text-gray-500 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase">Başlık</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#fd7000]/50 transition-colors"
                                    placeholder="Örn: Hetzner Main Key"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase">Tür</label>
                                    <select
                                        className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#fd7000]/50 transition-colors appearance-none"
                                        value={formData.type}
                                        onChange={e => setFormData({ ...formData, type: e.target.value })}
                                    >
                                        <option value="ssh-key">SSH Key</option>
                                        <option value="ssh-password">SSH Password</option>
                                        <option value="api-key">API Key</option>
                                        <option value="other">Diğer</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase">Kullanıcı Adı</label>
                                    <input
                                        type="text"
                                        disabled={formData.type === 'api-key'}
                                        className={`w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#fd7000]/50 transition-colors ${formData.type === 'api-key' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        placeholder={formData.type === 'api-key' ? 'N/A' : 'root'}
                                        value={formData.type === 'api-key' ? '' : formData.username}
                                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase">
                                    {formData.type === 'ssh-key' ? 'Private Key' :
                                        formData.type === 'api-key' ? 'API Key / Token' : 'Değer / Şifre'}
                                </label>
                                <textarea
                                    required
                                    rows={5}
                                    className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-[#fd7000]/50 transition-colors"
                                    placeholder={formData.type === 'ssh-key' ? "-----BEGIN OPENSSH PRIVATE KEY-----..." : "Gizli değer..."}
                                    value={formData.value}
                                    onChange={e => setFormData({ ...formData, value: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase">Açıklama</label>
                                <textarea
                                    rows={2}
                                    className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#fd7000]/50 transition-colors"
                                    placeholder="İsteğe bağlı açıklama..."
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/5">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg text-sm font-medium transition-colors"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={processing}
                                    className="flex items-center gap-2 px-6 py-2 bg-[#fd7000] hover:bg-[#e66000] text-white rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#fd7000]/20 disabled:opacity-50"
                                >
                                    {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    {modalMode === 'add' ? 'Oluştur' : 'Kaydet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CredentialsModule;
